import type { OperationStatus } from 'src/pages/admin/services/vaultOperationsService';

export const OPERATIONS_PERMISSION_RESOURCE = 'vault:operations';

export const OPERATIONS_NS = 'module.vaultOperations';

/** XBIT-4950: "Waiting for signers" is the old Approvals page folded in; it keeps its own permission, and a tab is hidden without it. */
export const SIGNERS_PERMISSION_RESOURCE = 'vault:approvals';

/** The permission each tab needs (read): the Operations console's own queues, and the two tabs on GET /vault/approvals ("Waiting for signers" the former Approvals page, "Stopped payouts" its in-flight half). */
export const TAB_PERMISSION_RESOURCE = (tab: string): string =>
  tab === 'signers' || tab === 'held' ? SIGNERS_PERMISSION_RESOURCE : OPERATIONS_PERMISSION_RESOURCE;

/** One host serves every stage, so this is a constant and not an env var. */
export const TURNKEY_ACTIVITY_URL = 'https://app.turnkey.com/activities';

/** Ops-console-ux rule 6, in the order the page shows them. */
export const OPERATIONS_TABS = [
  'frozen',
  'payouts',
  'closures',
  'transfers',
  'signers',
  'held',
  'history',
] as const;
export type OperationsTab = (typeof OPERATIONS_TABS)[number];

/** Lists are read no faster than every 15 s (XBIT-4870); the queues move on the scale of hours. */
export const POLL_INTERVAL_MS = 30_000;

/** The BFF's bound on notes, evidence links and hold reasons (`@Length(1, 500)`). */
export const TEXT_MAX = 500;

/** `ProposeClosureResolutionDto`: recovered is up to 8 dp (closedFraction is sent as a decimal string built from the percent). */
export const RECOVERED_PATTERN = /^\d+(\.\d{1,8})?$/;

/** Buttons on this page are sentence case: a long outcome label in capitals is hard to read ("Refund 120.00 USDC to the sender"). */
export const OUTCOME_BUTTON_SX = { textTransform: 'none' } as const;

export const HISTORY_PAGE_SIZE = 25;

/**
 * The runbook of each tab. The specs repo's default branch is `main`; the engine repo has no `main`, its default is `unstable`.
 * Transfers: the quarantined-transfer case sits under "2. Investigate" of the freeze runbook. History: the index of all runbooks.
 */
const SPECS = 'https://gitlab.ggwp.life/xbit/xbit-dex/xbit-vault-specs/-/blob/main/docs/08-ops';
const ENGINE =
  'https://gitlab.ggwp.life/xbit/xbit-dex/xbit-vault-engine/-/blob/unstable/docs/runbooks';
export const RUNBOOK_URLS: Record<OperationsTab, string> = {
  frozen: `${SPECS}/03-reconciliation-freeze.md`,
  held: `${ENGINE}/18-treasury-company-wallet.md`,
  closures: `${SPECS}/01-escalated-closure.md`,
  payouts: `${ENGINE}/18-treasury-company-wallet.md#6a-resolving-an-unresolved-order-xbit-4866`,
  transfers: `${SPECS}/03-reconciliation-freeze.md#2-investigate`,
  signers: `${ENGINE}/18-treasury-company-wallet.md`,
  history: `${SPECS}/00-index.md`,
};

/** Where an address is explored; set per stage in .env.* (testnet below prod, mainnet on prod). */
export const EXPLORER_ADDRESS_URL =
  process.env.REACT_APP_VAULT_EXPLORER_ADDRESS_URL ||
  'https://app.hyperliquid.xyz/explorer/address/';

export interface StatusStyle {
  color: 'error' | 'warning' | 'info' | 'default' | 'success';
  /** Held by operator is purple; MUI has no such palette colour. */
  purple?: boolean;
}

/** Ops-console-ux rule 3: colour + word + one-line hover (the words are in i18n `status` / `statusHint`). */
export const STATUS_STYLES: Record<OperationStatus, StatusStyle> = {
  NEEDS_ACTION: { color: 'error' },
  WAITING_SECOND_APPROVAL: { color: 'warning' },
  IN_PROGRESS: { color: 'info' },
  WAITING_AUTOMATIC: { color: 'default' },
  DONE: { color: 'success' },
  CANCELLED: { color: 'default' },
  // XBIT-5003: a payout a freeze or a lane pause has stopped. Warning, not default: it is money
  // that is not moving, and the greyest chip in the vocabulary understated that.
  STOPPED: { color: 'warning' },
  HELD: { color: 'default', purple: true },
};
